import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronUp, ChevronDown, Loader2, History, Ban, Pencil } from "lucide-react";
import Layout from "@/components/Layout";
import { Panel, EmptyState, MachineBadge } from "@/components/widgets";
import { useFilter } from "@/context/FilterContext";
import { useAuth } from "@/context/AuthContext";
import { useMasters } from "@/lib/useMasters";
import client, { formatApiError } from "@/lib/api";
import { fmtKg, fmtInt, fmtDate, fmtDateTime } from "@/lib/format";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

const COLS = [
  { key: "nestingDate", label: "Date", sortable: true },
  { key: "customer", label: "Customer", sortable: true },
  { key: "jobReference", label: "Job Ref", sortable: true },
  { key: "programmer", label: "Programmer" },
  { key: "materialGrade", label: "Grade" },
  { key: "thickness", label: "Thk" },
  { key: "machine", label: "Machine", sortable: true },
  { key: "weightKg", label: "Weight (kg)", sortable: true, right: true },
  { key: "status", label: "Status" },
];

function StatusChip({ v }) {
  const map = { active: { bg: "#DCFCE7", c: "#15803D" }, corrected: { bg: "#FEF3C7", c: "#B45309" }, voided: { bg: "#FEE2E2", c: "#B91C1C" } };
  const st = map[v] || map.active;
  return <span className="text-xs font-medium px-2 py-0.5 rounded capitalize" style={{ background: st.bg, color: st.c }}>{v}</span>;
}

export default function NestingRecords() {
  const { query, reset } = useFilter();
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [sort, setSort] = useState("nestingDate");
  const [dir, setDir] = useState("desc");
  const [detailId, setDetailId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["records", query, page, size, sort, dir],
    queryFn: () => client.get(`/nesting-records?${query}&page=${page}&size=${size}&sort=${sort}&direction=${dir}`).then((r) => r.data),
  });

  const toggleSort = (key) => {
    if (sort === key) setDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSort(key); setDir("desc"); }
    setPage(1);
  };

  const rows = data?.data || [];
  const pg = data?.page;

  const cell = (r, key) => {
    if (key === "nestingDate") return <span className="whitespace-nowrap text-slate-600">{fmtDate(r.nestingDate)}</span>;
    if (key === "customer") return <span className="truncate block max-w-[180px]">{r.customer.displayName}</span>;
    if (key === "jobReference") return <span className="font-mono text-xs">{r.jobReference}</span>;
    if (key === "programmer") return r.programmer.displayName;
    if (key === "materialGrade") return <span className="text-xs text-slate-500">{r.materialGrade.displayName}</span>;
    if (key === "thickness") return <span className="font-mono text-xs">{r.thickness.displayName}</span>;
    if (key === "machine") return <MachineBadge code={r.machine.code} />;
    if (key === "weightKg") return <span className="font-mono">{fmtKg(r.weightKg)}</span>;
    if (key === "status") return <StatusChip v={r.status} />;
    return null;
  };

  return (
    <Layout title="Nesting Records" subtitle="Searchable permanent source ledger"
      meta={pg ? `${fmtInt(pg.totalElements)} records` : null}>
      <Panel className="!p-0 overflow-hidden" testid="records-panel">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-slate-50">
              <tr className="text-left text-xs text-slate-500 border-b" style={{ borderColor: "#E2E8F0" }}>
                {COLS.map((c) => (
                  <th key={c.key} className={`py-2.5 px-3 font-medium ${c.right ? "text-right" : ""} ${c.sortable ? "cursor-pointer select-none hover:text-slate-800" : ""}`}
                    onClick={() => c.sortable && toggleSort(c.key)} data-testid={`col-${c.key}`}>
                    <span className={`inline-flex items-center gap-1 ${c.right ? "justify-end" : ""}`}>
                      {c.label}
                      {sort === c.key && (dir === "desc" ? <ChevronDown size={13} /> : <ChevronUp size={13} />)}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="border-b" style={{ borderColor: "#F1F5F9" }}>
                    {COLS.map((c) => <td key={c.key} className="py-2.5 px-3"><Skeleton className="h-4 w-16" /></td>)}
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr><td colSpan={COLS.length}><EmptyState onReset={reset} /></td></tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id} onClick={() => setDetailId(r.id)} data-testid={`record-row-${r.id}`}
                    className="border-b hover:bg-violet-50/40 cursor-pointer" style={{ borderColor: "#F1F5F9" }}>
                    {COLS.map((c) => <td key={c.key} className={`py-2.5 px-3 ${c.right ? "text-right" : ""}`}>{cell(r, c.key)}</td>)}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pg && (
          <div className="flex items-center justify-between px-4 py-3 border-t flex-wrap gap-2" style={{ borderColor: "#E2E8F0" }}>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span>Rows:</span>
              <Select value={String(size)} onValueChange={(v) => { setSize(Number(v)); setPage(1); }}>
                <SelectTrigger className="h-8 w-16" data-testid="page-size"><SelectValue /></SelectTrigger>
                <SelectContent>{[25, 50, 100].map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}</SelectContent>
              </Select>
              <span className="ml-2">Page {pg.number} of {pg.totalPages} · {fmtInt(pg.totalElements)} total</span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="prev-page">Previous</Button>
              <Button variant="outline" size="sm" disabled={page >= pg.totalPages} onClick={() => setPage((p) => p + 1)} data-testid="next-page">Next</Button>
            </div>
          </div>
        )}
      </Panel>

      {detailId && <RecordDetail id={detailId} onClose={() => setDetailId(null)} />}
    </Layout>
  );
}

function RecordDetail({ id, onClose }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const { data: masters } = useMasters();
  const [mode, setMode] = useState(null); // 'edit' | 'void'
  const [reason, setReason] = useState("");
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["record", id],
    queryFn: () => client.get(`/nesting-records/${id}`).then((r) => r.data),
  });

  const rec = data?.data;

  const openEdit = () => {
    if (mode === "edit") { setMode(null); return; }
    setForm({
      date: rec.nestingDate,
      jobReference: rec.jobReference,
      customerId: rec.customer.id,
      programmerId: rec.programmer.id,
      materialGradeId: rec.materialGrade.id,
      materialOwnershipId: rec.materialOwnership.id,
      thicknessId: rec.thickness.id,
      machineId: rec.machine.id,
      weightKg: String(rec.weightKg),
    });
    setReason("");
    setMode("edit");
  };
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const doEdit = async () => {
    if (!reason.trim()) return toast.error("A reason is required");
    if (!form.jobReference.trim()) return toast.error("Job reference cannot be blank");
    if (form.weightKg === "" || isNaN(parseFloat(form.weightKg)) || parseFloat(form.weightKg) < 0) return toast.error("Weight cannot be negative");
    setBusy(true);
    try {
      await client.patch(`/nesting-records/${id}`, {
        date: form.date,
        jobReference: form.jobReference,
        customerId: form.customerId,
        programmerId: form.programmerId,
        materialGradeId: form.materialGradeId,
        materialOwnershipId: form.materialOwnershipId,
        thicknessId: form.thicknessId,
        machineId: form.machineId,
        weightKg: parseFloat(form.weightKg),
        reason,
        expectedVersion: rec.version,
      });
      toast.success("Record updated");
      setMode(null); setReason("");
      await refetch(); qc.invalidateQueries();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "VERSION_CONFLICT") { toast.error("Record changed by someone else."); await refetch(); }
      else toast.error(formatApiError(d));
    } finally { setBusy(false); }
  };

  const doVoid = async () => {
    if (!reason.trim()) return toast.error("A reason is required");
    setBusy(true);
    try {
      await client.post(`/nesting-records/${id}/void`, { reason });
      toast.success("Record voided");
      setMode(null); setReason("");
      await refetch(); qc.invalidateQueries();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="record-detail">
        <SheetHeader>
          <SheetTitle className="font-head">Nesting Record</SheetTitle>
          <SheetDescription className="sr-only">Resolved record values, correction and void actions, and full audit history.</SheetDescription>
        </SheetHeader>
        {isLoading || !rec ? (
          <div className="mt-6 space-y-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
        ) : (
          <div className="mt-5 space-y-5">
            <div className="flex items-center gap-2">
              <StatusChip v={rec.status} /><MachineBadge code={rec.machine.code} />
              <span className="ml-auto font-mono text-xs text-slate-400">v{rec.version}</span>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Field label="Date" value={fmtDate(rec.nestingDate)} />
              <Field label="Job Reference" value={rec.jobReference} mono />
              <Field label="Customer" value={rec.customer.displayName} />
              <Field label="Programmer" value={rec.programmer.displayName} />
              <Field label="Material Grade" value={rec.materialGrade.displayName} />
              <Field label="Ownership" value={rec.materialOwnership.displayName} />
              <Field label="Thickness" value={rec.thickness.displayName} mono />
              <Field label="Processed Weight" value={`${fmtKg(rec.weightKg)} kg`} mono />
            </div>
            {rec.voidReason && <p className="text-xs text-red-600 bg-red-50 rounded p-2">Void reason: {rec.voidReason}</p>}

            {/* actions */}
            {rec.status !== "voided" && (
              <div className="flex gap-2">
                {can("nesting.correct") && (
                  <Button variant="outline" size="sm" onClick={openEdit} data-testid="correct-btn">
                    <Pencil size={14} className="mr-1" /> Edit record
                  </Button>
                )}
                {can("nesting.void") && (
                  <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setMode(mode === "void" ? null : "void")} data-testid="void-btn">
                    <Ban size={14} className="mr-1" /> Void
                  </Button>
                )}
              </div>
            )}

            {mode === "edit" && form && (
              <div className="panel p-4 space-y-3 bg-slate-50" data-testid="edit-form">
                <p className="text-xs font-semibold text-slate-700">Edit record — all fields</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] text-slate-500 mb-1 block">Date</label>
                    <Input type="date" value={form.date} onChange={(e) => setF("date", e.target.value)} data-testid="edit-date" />
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-500 mb-1 block">Job reference</label>
                    <Input value={form.jobReference} onChange={(e) => setF("jobReference", e.target.value)} data-testid="edit-jobref" />
                  </div>
                  <EditSelect label="Customer" value={form.customerId} onChange={(v) => setF("customerId", v)} options={masters?.customers} testid="edit-customer" />
                  <EditSelect label="Programmer" value={form.programmerId} onChange={(v) => setF("programmerId", v)} options={masters?.programmers} testid="edit-programmer" />
                  <EditSelect label="Material grade" value={form.materialGradeId} onChange={(v) => setF("materialGradeId", v)} options={masters?.grades} testid="edit-grade" />
                  <EditSelect label="Ownership" value={form.materialOwnershipId} onChange={(v) => setF("materialOwnershipId", v)} options={masters?.ownership} testid="edit-ownership" />
                  <EditSelect label="Thickness" value={form.thicknessId} onChange={(v) => setF("thicknessId", v)} options={masters?.thicknesses} testid="edit-thickness" />
                  <EditSelect label="Machine" value={form.machineId} onChange={(v) => setF("machineId", v)} options={masters?.machines} testid="edit-machine" />
                  <div className="col-span-2">
                    <label className="text-[11px] text-slate-500 mb-1 block">Processed weight (kg)</label>
                    <Input type="number" step="0.001" value={form.weightKg} onChange={(e) => setF("weightKg", e.target.value)} data-testid="edit-weight" />
                  </div>
                </div>
                <div>
                  <label className="text-[11px] text-slate-500 mb-1 block">Reason for change (required, kept in audit trail)</label>
                  <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Reassigned to correct customer account" data-testid="edit-reason" />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => setMode(null)}>Cancel</Button>
                  <Button size="sm" className="flex-1 text-white" style={{ background: "#0D9488" }} onClick={doEdit} disabled={busy} data-testid="edit-save">
                    {busy ? <Loader2 size={15} className="animate-spin" /> : "Save Changes"}
                  </Button>
                </div>
              </div>
            )}
            {mode === "void" && (
              <div className="panel p-3 space-y-2 bg-red-50">
                <p className="text-xs font-medium text-red-700">Void this record (kept for audit)</p>
                <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for voiding" data-testid="void-reason" />
                <Button size="sm" className="w-full" variant="destructive" onClick={doVoid} disabled={busy} data-testid="void-save">
                  {busy ? <Loader2 size={15} className="animate-spin" /> : "Confirm Void"}
                </Button>
              </div>
            )}

            {/* audit */}
            <div>
              <p className="text-sm font-semibold flex items-center gap-1.5 mb-2"><History size={15} /> Audit History</p>
              <div className="space-y-2">
                {(data.audit || []).length === 0 && <p className="text-xs text-slate-400">No audit events.</p>}
                {(data.audit || []).map((a) => (
                  <div key={a.id} className="text-xs border-l-2 pl-3 py-1" style={{ borderColor: "#C4B5FD" }} data-testid="audit-event">
                    <p className="font-medium text-slate-700 capitalize">{a.action.replace(/_/g, " ")}</p>
                    <p className="text-slate-500">{a.actor_name} · {fmtDateTime(a.occurred_at)}</p>
                    {a.reason && <p className="text-slate-400 italic">"{a.reason}"</p>}
                    {Array.isArray(a.new_values?.changes) && (
                      <ul className="mt-1 space-y-0.5">
                        {a.new_values.changes.map((c, i) => (
                          <li key={i} className="text-slate-600">
                            <span className="font-medium">{c.field}:</span>{" "}
                            <span className="text-red-500 line-through">{String(c.from)}</span>{" → "}
                            <span className="text-emerald-600">{String(c.to)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <p className="text-[11px] text-slate-400 uppercase tracking-wide">{label}</p>
      <p className={`text-slate-800 ${mono ? "font-mono text-sm" : ""}`}>{value}</p>
    </div>
  );
}

function EditSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-[11px] text-slate-500 mb-1 block">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent className="max-h-60">
          {(options || []).map((o) => <SelectItem key={o.id} value={o.id}>{o.display_name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
